import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useParams, Link, useNavigate } from "react-router-dom";
import { api, formatDate, timeAgo } from "@/lib/api";
import { toast } from "sonner";
import { resolveImageUrl } from "@/components/ImageUpload";
import { MapPin, Briefcase, Clock, IndianRupee, Building2, Search, Filter, ArrowLeft, Send, Bookmark, Calendar, ExternalLink, X, Upload, FileText, CheckCircle2, Loader2 } from "lucide-react";

const EMPLOYMENT_TYPES = [
  { v: "full-time", l: "Full-time" },
  { v: "part-time", l: "Part-time" },
  { v: "contract", l: "Contract" },
  { v: "temporary", l: "Temporary" },
  { v: "internship", l: "Internship" },
];
const WORK_MODES = [
  { v: "on-site", l: "On-site" },
  { v: "hybrid", l: "Hybrid" },
  { v: "remote", l: "Remote" },
];
const EMPLOYER_TYPES = [
  { v: "university", l: "University" },
  { v: "college", l: "College" },
  { v: "school", l: "School" },
  { v: "edtech", l: "EdTech" },
  { v: "supplier", l: "Supplier" },
  { v: "government", l: "Government" },
];

function formatSalary(min, max, currency = "INR", period = "year") {
  if (!min && !max) return null;
  const fmt = (n) => {
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)} Cr`;
    if (n >= 100000) return `₹${(n / 100000).toFixed(1)} L`;
    return `₹${(n / 1000).toFixed(0)}K`;
  };
  const per = period === "year" ? "/yr" : period === "month" ? "/mo" : "/hr";
  if (min && max) return `${fmt(min)} – ${fmt(max)} ${per}`;
  return `${fmt(min || max)} ${per}`;
}

export function JobsListPage() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("search") || "");

  const filters = useMemo(() => ({
    search: params.get("search") || undefined,
    subject: params.get("subject") || undefined,
    employer_type: params.get("employer_type") || undefined,
    employment_type: params.get("employment_type") || undefined,
    work_mode: params.get("work_mode") || undefined,
    state: params.get("state") || undefined,
  }), [params]);

  useEffect(() => {
    setLoading(true);
    api.get("/jobs", { params: { ...filters, limit: 30 } })
      .then(r => { setItems(r.data.items); setTotal(r.data.total); })
      .finally(() => setLoading(false));
  }, [filters]);
  useEffect(() => { api.get("/jobs/facets").then(r => setFacets(r.data)); }, []);

  const setFilter = (k, v) => {
    const p = new URLSearchParams(params);
    if (!v || v === params.get(k)) p.delete(k); else p.set(k, v);
    setParams(p);
  };
  const clearAll = () => setParams({});
  const submitSearch = (e) => { e.preventDefault(); const p = new URLSearchParams(params); if (search) p.set("search", search); else p.delete("search"); setParams(p); };

  const activeCount = Object.values(filters).filter(Boolean).length;

  return (
    <div data-testid="jobs-page">
      {/* Hero */}
      <div className="bg-gradient-to-br from-sky-50 via-white to-emerald-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="overline text-sky-700 mb-2">EduTime Uni & School Jobs</div>
          <h1 className="headline text-4xl md:text-5xl font-bold text-slate-900 mb-3">Careers in Indian Education</h1>
          <p className="text-slate-600 max-w-2xl mb-6">Discover teaching, research, admin, and leadership roles across India's universities, schools, and EdTech companies.</p>
          <form onSubmit={submitSearch} className="flex flex-col sm:flex-row gap-2 max-w-3xl" data-testid="jobs-search-form">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Job title, subject, employer…" className="w-full pl-11 pr-4 py-3.5 rounded-full bg-white border border-slate-200 focus:border-sky-400 outline-none text-sm" data-testid="jobs-search-input"/>
            </div>
            <button className="px-8 py-3.5 rounded-full bg-sky-700 text-white font-semibold text-sm hover:bg-sky-800" data-testid="jobs-search-submit">Search</button>
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 grid lg:grid-cols-4 gap-8">
        {/* Filters */}
        <aside className="lg:col-span-1 space-y-6" data-testid="jobs-filters">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sky-700">
              <Filter className="w-4 h-4"/><span className="overline">Filters {activeCount > 0 && `(${activeCount})`}</span>
            </div>
            {activeCount > 0 && <button onClick={clearAll} className="text-xs text-slate-500 hover:text-sky-700" data-testid="jobs-clear-filters">Clear all</button>}
          </div>

          <FacetGroup title="Subject area" values={facets.subject} active={filters.subject} onPick={v => setFilter("subject", v)}/>
          <FacetGroup title="Contract" values={EMPLOYMENT_TYPES.map(x => ({ value: x.v, label: x.l, count: (facets.employment_type || []).find(f => f.value === x.v)?.count }))} active={filters.employment_type} onPick={v => setFilter("employment_type", v)}/>
          <FacetGroup title="Work mode" values={WORK_MODES.map(x => ({ value: x.v, label: x.l, count: (facets.work_mode || []).find(f => f.value === x.v)?.count }))} active={filters.work_mode} onPick={v => setFilter("work_mode", v)}/>
          <FacetGroup title="Employer type" values={EMPLOYER_TYPES.map(x => ({ value: x.v, label: x.l, count: (facets.employer_type || []).find(f => f.value === x.v)?.count }))} active={filters.employer_type} onPick={v => setFilter("employer_type", v)}/>
          <FacetGroup title="Location (state)" values={facets.state} active={filters.state} onPick={v => setFilter("state", v)}/>
        </aside>

        {/* Results */}
        <section className="lg:col-span-3 space-y-4" data-testid="jobs-results">
          <div className="flex items-baseline justify-between">
            <div className="text-sm text-slate-600"><strong className="text-slate-900">{total}</strong> job{total !== 1 && "s"} found</div>
          </div>
          {loading ? <div className="text-slate-500">Loading jobs…</div> :
            items.length === 0 ? <div className="card-soft p-12 text-center text-slate-500" data-testid="jobs-empty">No jobs match your filters. Try clearing them.</div> :
            <div className="space-y-3">{items.map(j => <JobCard key={j.id} job={j}/>)}</div>}
        </section>
      </div>
    </div>
  );
}

function FacetGroup({ title, values = [], active, onPick }) {
  if (!values || !values.length) return null;
  return (
    <div className="border-b border-slate-100 pb-4">
      <div className="text-xs font-semibold text-slate-800 mb-2">{title}</div>
      <ul className="space-y-1">
        {values.slice(0, 8).map(v => (
          <li key={v.value}>
            <button onClick={() => onPick(v.value)} className={`w-full text-left px-2 py-1 rounded-md text-xs flex justify-between items-center ${active === v.value ? "bg-sky-100 text-sky-800 font-semibold" : "text-slate-700 hover:bg-slate-50"}`} data-testid={`facet-${title.toLowerCase().replace(/\s+/g, "-")}-${v.value}`}>
              <span>{v.label || v.value}</span>
              {v.count != null && <span className="text-[10px] text-slate-400">{v.count}</span>}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function JobCard({ job }) {
  const salary = formatSalary(job.salary_min, job.salary_max, job.salary_currency, job.salary_period);
  return (
    <Link to={`/job/${job.slug}`}
      className="block p-5 rounded-2xl bg-emerald-50/70 border border-emerald-100 hover:bg-emerald-50 hover:border-emerald-300 hover:shadow-[0_12px_32px_rgba(16,185,129,0.10)] hover:-translate-y-0.5 transition-all duration-200 flex gap-4 items-start hover-zoom"
      data-testid={`job-card-${job.id}`}>
      <div className="w-14 h-14 rounded-xl bg-white border border-emerald-100 grid place-items-center shrink-0 overflow-hidden shadow-sm">
        {job.employer_logo
          ? <img src={resolveImageUrl(job.employer_logo)} alt="" className="w-full h-full object-cover"/>
          : <Building2 className="w-6 h-6 text-emerald-700"/>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          {job.is_featured && <span className="overline text-[10px] px-2 py-1 rounded-full bg-emerald-600 text-white">Featured</span>}
          {job.is_sponsored && <span className="overline text-[10px] px-2 py-1 rounded-full bg-amber-100 text-amber-800">Sponsored</span>}
          <span className="overline text-[10px] text-emerald-700/80">{job.employer_type}</span>
        </div>
        <h3 className="font-editorial text-lg font-bold text-slate-900 leading-snug title-link">{job.title}</h3>
        <div className="text-sm text-emerald-900/80 mt-1 font-medium">{job.employer_name}</div>
        <div className="mt-3 flex flex-wrap gap-3 text-xs text-emerald-900/70">
          <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5"/>{[job.location_city, job.location_state].filter(Boolean).join(", ") || "Location N/A"}</span>
          <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5"/>{job.employment_type}</span>
          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5"/>{job.work_mode}</span>
          {salary && <span className="flex items-center gap-1 text-emerald-800 font-bold bg-white/70 px-2 py-0.5 rounded-full"><IndianRupee className="w-3.5 h-3.5"/>{salary}</span>}
        </div>
      </div>
      <div className="text-right text-[11px] text-emerald-800/70 whitespace-nowrap font-medium">{timeAgo(job.posted_at)}</div>
    </Link>
  );
}

export function JobDetailPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [j, setJ] = useState(null);
  const [openApply, setOpenApply] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    api.get(`/jobs/${slug}`).then(r => setJ(r.data)).catch(() => setJ(null));
  }, [slug]);

  if (!j) return <div className="max-w-4xl mx-auto px-4 py-20 text-slate-500" data-testid="job-loading">Loading…</div>;
  const salary = formatSalary(j.salary_min, j.salary_max, j.salary_currency, j.salary_period);

  return (
    <div className="max-w-6xl mx-auto px-4 py-10" data-testid="job-detail-page">
      <button onClick={() => nav(-1)} className="text-xs text-slate-500 flex items-center gap-1 mb-6 hover:text-emerald-700"><ArrowLeft className="w-3 h-3"/>Back to jobs</button>

      <div className="grid md:grid-cols-3 gap-8">
        <article className="md:col-span-2 p-6 md:p-8 rounded-3xl bg-emerald-50/70 border border-emerald-100" data-testid="job-detail-article">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-16 h-16 rounded-2xl bg-white border border-emerald-100 grid place-items-center overflow-hidden shrink-0 shadow-sm">
              {j.employer_logo ? <img src={resolveImageUrl(j.employer_logo)} alt="" className="w-full h-full object-cover"/> : <Building2 className="w-7 h-7 text-emerald-700"/>}
            </div>
            <div>
              <div className="overline text-emerald-700 mb-0.5">{j.employer_type}</div>
              <div className="font-editorial text-lg font-bold text-slate-900">{j.employer_name}</div>
            </div>
          </div>
          <h1 className="headline text-3xl md:text-4xl font-bold text-slate-900 mb-3" data-testid="job-title">{j.title}</h1>
          <div className="flex flex-wrap gap-3 text-sm text-emerald-900/80 mb-6">
            <span className="flex items-center gap-1"><MapPin className="w-4 h-4"/>{[j.location_city, j.location_state, j.location_country].filter(Boolean).join(", ")}</span>
            <span className="flex items-center gap-1"><Briefcase className="w-4 h-4"/>{j.employment_type}</span>
            <span className="flex items-center gap-1"><Clock className="w-4 h-4"/>{j.work_mode}</span>
            {j.subject && <span className="overline text-[10px] px-2 py-1 rounded-full bg-emerald-100 text-emerald-800">{j.subject}</span>}
          </div>
          <div className="prose-editorial text-slate-800 bg-white/70 rounded-2xl p-6 border border-emerald-50" dangerouslySetInnerHTML={{ __html: j.description }} data-testid="job-description"/>

          {j.related?.length > 0 && (
            <section className="mt-10">
              <h2 className="font-editorial text-2xl font-bold mb-4 text-slate-900">Similar roles</h2>
              <div className="space-y-3">{j.related.map(r => <JobCard key={r.slug} job={r}/>)}</div>
            </section>
          )}
        </article>

        <aside className="space-y-4">
          <div className="p-6 rounded-3xl bg-emerald-50/70 border border-emerald-100 sticky top-24">
            {salary && <div className="mb-4"><div className="overline text-emerald-700 mb-1">Salary</div><div className="font-editorial text-2xl font-bold text-emerald-800">{salary}</div></div>}
            {j.apply_deadline && <div className="mb-4"><div className="overline text-emerald-700 mb-1">Closing date</div><div className="text-sm font-semibold text-slate-800 flex items-center gap-1"><Calendar className="w-3.5 h-3.5"/>{formatDate(j.apply_deadline)}</div></div>}
            <div className="mb-4"><div className="overline text-emerald-700 mb-1">Posted</div><div className="text-sm text-slate-700">{timeAgo(j.posted_at)}</div></div>
            <div className="flex flex-col gap-2">
              {j.application_url
                ? <a href={j.application_url} target="_blank" rel="noreferrer" className="w-full py-3 rounded-full bg-emerald-700 text-white font-semibold text-sm flex items-center justify-center gap-1 hover:bg-emerald-800" data-testid="job-apply-external"><ExternalLink className="w-4 h-4"/>Apply on employer site</a>
                : <button onClick={() => setOpenApply(true)} className="w-full py-3 rounded-full bg-emerald-700 text-white font-semibold text-sm hover:bg-emerald-800" data-testid="job-apply-btn">Apply Now</button>}
              <button className="w-full py-3 rounded-full bg-white text-emerald-800 text-sm font-semibold flex items-center justify-center gap-1 border border-emerald-200 hover:bg-emerald-50"><Bookmark className="w-4 h-4"/>Save job</button>
            </div>
            {j.application_email && !j.application_url && <div className="mt-4 pt-4 border-t border-emerald-100 text-xs text-emerald-900/70">Or email your CV to <a href={`mailto:${j.application_email}`} className="text-emerald-700 font-semibold">{j.application_email}</a></div>}
          </div>
        </aside>
      </div>

      {openApply && <ApplyModal job={j} onClose={() => setOpenApply(false)}/>}
    </div>
  );
}

function ApplyModal({ job, onClose }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", cv_url: "", linkedin_url: "", cover_note: "" });
  const [cvFile, setCvFile] = useState(null); // { name, size }
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const uploadCv = async (file) => {
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["pdf", "doc", "docx"].includes(ext)) {
      toast.error("Only PDF, DOC, or DOCX files are allowed");
      return;
    }
    if (file.size > 3 * 1024 * 1024) { toast.error("Max 3 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/jobs/upload-cv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF(prev => ({ ...prev, cv_url: data.url }));
      setCvFile({ name: data.filename || file.name, size: data.size });
      toast.success("CV uploaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const removeCv = () => { setCvFile(null); setF(prev => ({ ...prev, cv_url: "" })); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/jobs/${job.slug}/apply`, f);
      setDone(true);
      toast.success("Application sent!");
    } catch (e) { toast.error(e.response?.data?.detail || "Application failed"); }
    finally { setBusy(false); }
  };

  const fmtSize = (b) => b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b/1024).toFixed(1)} KB` : `${(b/1024/1024).toFixed(1)} MB`;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="apply-modal">
      <div className="bg-white rounded-3xl max-w-2xl w-full p-8 sm:p-10 relative max-h-[92vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100" data-testid="apply-close"><X className="w-4 h-4"/></button>
        {done ? (
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-emerald-100 grid place-items-center mx-auto mb-4"><CheckCircle2 className="w-8 h-8 text-emerald-700"/></div>
            <h2 className="font-editorial text-2xl font-bold mb-2">Application received!</h2>
            <p className="text-slate-600">Your details have been forwarded to {job.employer_name}. Best of luck!</p>
            <button onClick={onClose} className="mt-6 px-6 py-2.5 rounded-full bg-emerald-700 text-white font-semibold text-sm">Close</button>
          </div>
        ) : (
          <>
            <div className="overline text-emerald-700 mb-1">Apply to</div>
            <h2 className="font-editorial text-2xl font-bold text-slate-900 leading-tight mb-1">{job.title}</h2>
            <p className="text-sm text-slate-500 mb-6">at {job.employer_name}</p>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input required placeholder="Full name" value={f.name} onChange={e => setF({...f, name: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-emerald-400" data-testid="apply-name"/>
                <input required type="email" placeholder="Email" value={f.email} onChange={e => setF({...f, email: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-emerald-400" data-testid="apply-email"/>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input placeholder="Phone (optional)" value={f.phone} onChange={e => setF({...f, phone: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-emerald-400" data-testid="apply-phone"/>
                <input type="url" placeholder="LinkedIn profile URL (optional)" value={f.linkedin_url} onChange={e => setF({...f, linkedin_url: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-emerald-400" data-testid="apply-linkedin"/>
              </div>

              {/* CV upload */}
              <div>
                <div className="overline text-xs text-slate-600 mb-1.5">Attach CV (PDF / DOC / DOCX · max 3 MB)</div>
                {cvFile ? (
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-100" data-testid="apply-cv-uploaded">
                    <div className="w-10 h-10 rounded-lg bg-white border border-emerald-100 grid place-items-center"><FileText className="w-5 h-5 text-emerald-700"/></div>
                    <div className="flex-1 min-w-0">
                      <div className="font-editorial font-semibold text-slate-900 truncate">{cvFile.name}</div>
                      <div className="text-[11px] text-emerald-800/70">{fmtSize(cvFile.size)} · uploaded</div>
                    </div>
                    <button type="button" onClick={removeCv} className="p-2 rounded-lg hover:bg-red-50 text-red-500" data-testid="apply-cv-remove"><X className="w-4 h-4"/></button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center gap-2 py-6 rounded-xl border-2 border-dashed border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/40 cursor-pointer transition-colors" data-testid="apply-cv-picker">
                    {uploading ? <Loader2 className="w-6 h-6 text-emerald-700 animate-spin"/> : <Upload className="w-6 h-6 text-slate-400"/>}
                    <span className="text-sm text-slate-600 font-medium">{uploading ? "Uploading…" : "Click to choose your CV"}</span>
                    <span className="text-[10px] text-slate-400">PDF · DOC · DOCX only</span>
                    <input type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      className="hidden" onChange={e => uploadCv(e.target.files?.[0])} data-testid="apply-cv-input"/>
                  </label>
                )}
              </div>

              <textarea rows={4} placeholder="Short cover note (optional)" value={f.cover_note} onChange={e => setF({...f, cover_note: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-emerald-400" data-testid="apply-cover-note"/>
              <button disabled={busy || uploading} className="w-full py-3 rounded-full bg-emerald-700 text-white font-semibold text-sm flex items-center justify-center gap-1 disabled:opacity-60 hover:bg-emerald-800" data-testid="apply-submit"><Send className="w-4 h-4"/>{busy ? "Sending…" : "Submit Application"}</button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
